// LightPlane
// Features:
// - Renders light shafts projecting from windows
// - Visibility toggled by global light state

import { useMemo, useRef } from 'react'
import { useTexture } from '@react-three/drei'
import { useFrame } from '@react-three/fiber'
import * as THREE from 'three'

import { useStore } from '../../store/useStore'

import vertexShader from '../shaders/lightPlane/vertex.glsl'
import fragmentShader from '../shaders/lightPlane/fragment.glsl'

import alphaMapURL from '/textures/window_light.png'

const PLANE_CONFIG = {
    lightColor: new THREE.Color('#ffc88a'),
    w1Pos: new THREE.Vector2(0.34, 0.68),
    w1Rotation: 2.16,
    w1Scale: 0.14,
    w1Offset: new THREE.Vector2(-0.5, 0.0),
    w2Pos: new THREE.Vector2(0.66, 0.67),
    w2Rotation: -2.51,
    w2Scale: 0.15,
    w2Offset: new THREE.Vector2(0.5, 0.0),
    alpha: 0.04,
    layer2ScaleMul: 0.83,
    layer2AlphaBoost: 2.26,
}

export default function LightPlane({ nodes }) {
    const lightsOn = useStore((state) => state.lightsOn)
    const materialRef = useRef()

    // Textures
    const alphaMap = useTexture(alphaMapURL, (texture) => {
        texture.flipY = false
    })

    // Material
    const material = useMemo(() => {
        return new THREE.ShaderMaterial({
            uniforms: {
                uAlphaMap: { value: alphaMap },
                uLightColor: { value: PLANE_CONFIG.lightColor },
                uWindow1Pos: { value: PLANE_CONFIG.w1Pos },
                uWindow1Rotation: { value: PLANE_CONFIG.w1Rotation },
                uWindow1Scale: { value: PLANE_CONFIG.w1Scale },
                uWindow1SampleOffset: { value: PLANE_CONFIG.w1Offset },
                uWindow2Pos: { value: PLANE_CONFIG.w2Pos },
                uWindow2Rotation: { value: PLANE_CONFIG.w2Rotation },
                uWindow2Scale: { value: PLANE_CONFIG.w2Scale },
                uWindow2SampleOffset: { value: PLANE_CONFIG.w2Offset },
                uAlpha: { value: PLANE_CONFIG.alpha },
                uSecondScaleMul: { value: PLANE_CONFIG.layer2ScaleMul },
                uSecondAlphaBoost: { value: PLANE_CONFIG.layer2AlphaBoost },
                uTime: { value: 0 },
            },
            vertexShader,
            fragmentShader,
            transparent: true,
        })
    }, [alphaMap])

    // Animation Loop
    useFrame((state) => {
        if (materialRef.current) {
            materialRef.current.uniforms.uTime.value = state.clock.elapsedTime
        }
    })

    if (!lightsOn) return null

    return (
        <group>
            <mesh
                ref={(mesh) => {
                    if (mesh) materialRef.current = mesh.material
                }}
                geometry={nodes.light_plane.geometry}
                material={material}
                position={[2.039, -0.189, 0.014]}
                rotation={[0, -0.611, 0]}
            />
        </group>
    )
}
